/**
 * API Client Interceptors
 *
 * Configures and wires interceptors for the XP and Content API clients.
 * Handles authentication, authorization, and impersonation errors at the
 * request/response level.
 *
 * This module runs as a side effect on import (see bottom of file).
 * Import it in main.tsx like the other side-effect modules:
 *   import './services/api-interceptors';
 *
 * IMPORTANT: We use RESPONSE interceptors (not error interceptors) because:
 * - Response interceptors run BEFORE the response.ok check
 * - We can convert error responses to "fake OK" responses to prevent React Query errors
 * - Error interceptors run too late - the error still gets thrown even if we return undefined
 */

import { client as xpClient } from '@cfa/pathway-fe-shared/xp';

import {
  attemptSessionRecovery,
  buildErrorInfoFromResponse,
  handleApiError,
  isReadOnlyRequest,
} from '@/utils/api-error-handler';
import { getApiToken } from '@/utils/auth';
import { getFakeTokenHeader } from '@/utils/permissions-widget/token';

import Constants from '@/constants';

import { setupPlanResourceDebugMockInterceptor } from './planResourceDebugMockInterceptor';

import { client as contentClient } from '@/_generated/api-clients/content/client.gen';
import { store as jotaiStore } from '@/atoms';
import { sessionOverridesAtom } from '@/atoms/sessionOverrides';

/**
 * Track handled errors to prevent duplicate toasts.
 * Errors are auto-cleared after a delay to prevent memory leaks
 * while still avoiding infinite toast loops during active error states.
 */
const handledAuthErrors = new Map<string, NodeJS.Timeout>();

/**
 * Generate a deduplication key for an error.
 * Uses URL to group errors from the same endpoint.
 */
const getErrorKey = (request: Request): string => request.url;

/**
 * Check if we've already handled this error recently.
 */
const hasHandledError = (key: string): boolean => handledAuthErrors.has(key);

/**
 * Mark an error as handled with auto-cleanup after 60 seconds.
 * This is long enough to prevent infinite loops during active error states,
 * but short enough to prevent memory leaks during long sessions.
 */
const markErrorHandled = (key: string): void => {
  const existingTimeout = handledAuthErrors.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeout = setTimeout(() => {
    handledAuthErrors.delete(key);
  }, 60_000);

  handledAuthErrors.set(key, timeout);
};

/**
 * Create a synthetic "OK" response to replace error responses we've handled.
 * This prevents the error from propagating to React Query.
 */
const createSyntheticOkResponse = (originalResponse: Response): Response => {
  const headers = new Headers(originalResponse.headers);
  headers.set('Content-Type', 'application/json');

  return new Response(JSON.stringify({}), {
    status: 200,
    statusText: 'OK (handled by interceptor)',
    headers,
  });
};

const createSyntheticOkResponseIfReadOnly = (
  request: Request,
  response: Response,
): Response =>
  isReadOnlyRequest(request) ? createSyntheticOkResponse(response) : response;

/**
 * Configure the XP API client with base URL, credentials, headers, and auth.
 */
const setupXpClient = (): void => {
  xpClient.setConfig({
    baseUrl: Constants.XP_API_BASE_URL,
    credentials: 'include',
    headers: getFakeTokenHeader(),
    auth: () => getApiToken(),
  });
};

/**
 * Setup request interceptor for a client.
 * Adds auth token and session override headers.
 */
const setupRequestInterceptor = (
  client: typeof xpClient | typeof contentClient,
): void => {
  client.interceptors.request.use(async req => {
    const token = await getApiToken();
    if (token) {
      req.headers.set('Authorization', `Bearer ${token}`);
    }

    const sessionOverrides = jotaiStore.get(sessionOverridesAtom);
    if (sessionOverrides.has('updateResource')) {
      req.headers.set('X-Pathway-Feature-HideAllXylemeReferences', 'true');
    }

    return req;
  });
};

/**
 * Setup response interceptor for a client.
 * Intercepts error responses BEFORE they become errors to prevent infinite loops.
 *
 * This runs before the response.ok check, so we can:
 * 1. Detect auth errors (401/403)
 * 2. Show toast and mark as handled
 * 3. Return a synthetic "OK" response to prevent error propagation
 */
const setupResponseInterceptor = (
  client: typeof xpClient | typeof contentClient,
): void => {
  client.interceptors.response.use(async (response, request, _options) => {
    if (response.ok) {
      return response;
    }

    const errorKey = getErrorKey(request);

    if (hasHandledError(errorKey)) {
      return createSyntheticOkResponseIfReadOnly(request, response);
    }

    const errorInfo = await buildErrorInfoFromResponse(response, request);
    const recovery = await attemptSessionRecovery(errorInfo, response, request);

    if (recovery.action === 'retry') {
      return recovery.response;
    }

    if (recovery.action === 'synthetic') {
      markErrorHandled(errorKey);
      return createSyntheticOkResponse(response);
    }

    const wasHandled = await handleApiError(errorInfo, 'Interceptor');

    if (wasHandled) {
      // Only mark read URLs handled when returning synthetic OK. Writes that
      // propagate must not poison later GET recovery on the same URL.
      if (isReadOnlyRequest(request)) {
        markErrorHandled(errorKey);
        return createSyntheticOkResponse(response);
      }
      return response;
    }

    return response;
  });
};

// Configure the XP client and register all interceptors on import.
// Mirrors the pattern used by src/utils/native-binding.ts.
setupXpClient();
setupRequestInterceptor(xpClient);
setupResponseInterceptor(xpClient);
setupRequestInterceptor(contentClient);
setupResponseInterceptor(contentClient);
setupPlanResourceDebugMockInterceptor();

console.debug('[API] Interceptors initialized for XP and Content clients');
